import { concatValuesBy, formatNumber } from '@miq/utils';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../models/report.model';
import { DisplayFnArgumentsInfo, LovValues, TemplateFieldsDisplayFn } from '../../../models/template.model';
import { Dash, displayFnWrapper, mapAndFormatDynamicValue } from '../utils/dynamic-data-retrievers';
import { LocaleKeys } from '../utils/template-display-sort-order.const';
import { submarketLov } from './common';

const CHINESE_TEXT = {
  part: '部分',
  wholeFloor: '整层',
};
const ENGLISH_TEXT = {
  part: 'Part',
  wholeFloor: 'Whole Floor',
};

export const displayFnMap: TemplateFieldsDisplayFn = {
  // property name
  49: {
    displayFn: displayFnWrapper,
  },
  // location
  50: {
    displayFn: (record: ReportRecord, options, submarket, district) => {
      return submarketLov(options).pipe(
        map((lov: LovValues[]) => {
          const lovKeyValue = mapAndFormatDynamicValue(record, submarket, lov, null, true);
          const lovValue = lov.find(a => a.key === lovKeyValue);
          const val = lovValue ? lovValue.value : '-';
          return concatValuesBy(', ', val, mapAndFormatDynamicValue(record, district, null, null, true));
        })
      );
    },
  },
  // Grade
  336: {
    displayFn: (record: ReportRecord, _, grade) => {
      const result = getGradeValue(record, grade);
      return from(result);
    },
  },
  // Completion Time
  337: {
    displayFn: displayFnWrapper,
  },
  // Landlord
  338: {
    displayFn: displayFnWrapper,
  },
  // Available Floor / Unit
  339: {
    displayFn: (record: ReportRecord, options, unit, floor, fullFloor) => {
      if (Array.isArray(record.data)) {
        const appendText = options.locale === LocaleKeys.ChinaChinese ? CHINESE_TEXT : ENGLISH_TEXT;
        const result = record.data.map(element => {
          const floorVal = mapAndFormatDynamicValue({ ...record, data: element }, floor);
          const isWholeFloor = mapAndFormatDynamicValue({ ...record, data: element }, fullFloor);
          return (floorVal as string).trim() + '/' + (isWholeFloor === 'Yes' ? appendText.wholeFloor : appendText.part);
        });
        return concatValuesBy(',', ...new Set(result));
      }
      return concatValuesBy(' | ', mapAndFormatDynamicValue(record, floor), mapAndFormatDynamicValue(record, unit));
    },
  },
  // Size (sqm gross area)
  340: {
    displayFn: (record: ReportRecord, _, askingRent) => {
      if (Array.isArray(record.data)) {
        const { entity, field, id } = askingRent;
        let total = 0;
        let totalComposition = [];
        record.data.forEach(avail => {
          const askingRentVal = mapAndFormatDynamicValue(
            { ...record, data: avail },
            { dataType: 'id', entity, field, id }
          );
          total = total + Number(askingRentVal[0]);
          totalComposition = [...totalComposition, formatNumber(askingRentVal[0], undefined, askingRent.dataFormat)];
        });
        const result =
          formatNumber(total, undefined, askingRent.dataFormat) + ` sqm(${concatValuesBy('+', ...totalComposition)})`;
        return result;
      } else {
        const result = getCurrencyLovValue(record, askingRent);
        return from(result);
      }
    },
  },
  // 交付状态
  126: {
    displayFn: displayFnWrapper,
  },
  // Available Date
  246: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  // Management Fee (RMB/sqm/month)
  341: {
    displayFn: displayFnWrapper,
  },
  // Walking Distance to Metro
  342: {
    displayFn: (record: ReportRecord, options, arg1, arg2, arg3) => {
      return arrangeArguments(record, options, arg1, arg2, arg3);
    },
  },
  // Asking Rent (RMB/sqm/day) (VAT inclusive)
  343: {
    displayFn: (record: ReportRecord, _, askingRent) => {
      if (Array.isArray(record.data)) {
        return Dash;
      } else {
        const result = getCurrencyLovValue(record, askingRent);
        return from(result);
      }
    },
  },
  // Rent Free (months)
  344: {
    displayFn: displayFnWrapper,
  },
  // Est. Effective Rental (RMB/sqm/day)
  345: {
    displayFn: displayFnWrapper,
  },
  // Remarks
  346: {
    displayFn: (record: ReportRecord, _options, arg) => {
      return Array.isArray(record.data) ? Dash : mapAndFormatDynamicValue(record, arg);
    },
  },
  98: {
    // 98 - Primary Image
    displayFn: record => {
      return record['data'].getMediaInformation();
    },
  },
};

async function getGradeValue(record: ReportRecord, grade: DisplayFnArgumentsInfo): Promise<string> {
  const uomLov: LovValues[] = await grade.lovValues.toPromise();
  const lovKeyValue = mapAndFormatDynamicValue(record, grade, uomLov);
  const lovValue = uomLov.find(a => a.key === lovKeyValue);
  return lovValue ? lovValue.value : '-';
}

async function getCurrencyLovValue(record: ReportRecord, arg1: DisplayFnArgumentsInfo): Promise<string | string[]> {
  const uomLov: LovValues[] = await arg1.uomArgument.lovValues.toPromise();
  const lovKeyValue = mapAndFormatDynamicValue(record, arg1, uomLov, true, true);
  return lovKeyValue;
}

function arrangeArguments(record: ReportRecord, options, arg1, arg2, arg3) {
  // english and chinese arguments are not in same order

  if (options.locale === LocaleKeys.ChinaChinese) {
    return concatValuesBy(
      ' ',
      mapAndFormatDynamicValue(record, arg3),
      '至',
      mapAndFormatDynamicValue(record, arg2),
      mapAndFormatDynamicValue(record, arg1)
    );
  }
  return concatValuesBy(
    ' ',
    mapAndFormatDynamicValue(record, arg2),
    'to',
    mapAndFormatDynamicValue(record, arg3),
    mapAndFormatDynamicValue(record, arg1)
  );
}
